import { Link, useLocation } from 'react-router-dom'
import { Coins } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { NavItem } from './Navbar'

interface MobileMenuUser {
  can_manage_credits?: boolean
}

interface MobileMenuPanelProps {
  navItems: NavItem[]
  mobileOpen: boolean
  onClose: () => void
  creditsDisplay: string
  user: MobileMenuUser
}

export function MobileMenuPanel({ navItems, mobileOpen, onClose, creditsDisplay, user }: MobileMenuPanelProps) {
  const location = useLocation()
  const isActive = (path: string) => location.pathname.startsWith(path)

  return (
    <div className={cn(
      "lg:hidden absolute top-full left-0 right-0 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 shadow-lg",
      "transition-all duration-300 ease-out overflow-hidden",
      mobileOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
    )}>
      <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.path)
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors",
                active
                  ? "text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30"
                  : item.danger
                    ? "text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                    : "text-neutral-800 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              )}
            >
              <Icon className="h-5 w-5" />
              <span>{item.label}</span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-2xs bg-primary-600 text-white rounded font-semibold">
                  {item.badge}
                </span>
              )}
            </Link>
          )
        })}

        {/* Mobile Credits */}
        {user.can_manage_credits && (
          <>
            <div className="my-2 border-t border-neutral-200 dark:border-neutral-700" />
            <Link
              to="/v2/credits"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
            >
              <Coins className="h-5 w-5" />
              <span>{creditsDisplay}</span>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
